import { format } from 'date-fns';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Clock,
  Factory,
  FileCheck,
  MoreHorizontal,
  Package,
  Truck,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  type PoMilestone,
  type PoMilestoneStatus,
  type PoMilestoneType,
  type UpdateMilestoneDto,
  milestoneTypeLabels,
  milestoneStatusColors,
} from '@/api/services/po-visibility.api';

/**
 * A purchase order's milestones as a vertical timeline.
 *
 * This lived inside PO Visibility's detail sheet, which meant the milestones —
 * the answer to "where is my order" — were only visible from the analytics page
 * and not on the purchase order's own page. The sheet is gone; this is now
 * rendered on /purchase-orders/:id, where the order actually lives.
 */
interface MilestoneTimelineProps {
  milestones: PoMilestone[];
  onUpdateMilestone: (id: string, data: UpdateMilestoneDto) => void;
}

export function MilestoneTimeline({ milestones, onUpdateMilestone }: MilestoneTimelineProps) {
  const getStatusIcon = (status: PoMilestoneStatus) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle2 className="h-5 w-5 text-emerald-600" />;
      case 'IN_PROGRESS':
        return <Clock className="h-5 w-5 text-blue-600 animate-pulse" />;
      case 'DELAYED':
        return <AlertTriangle className="h-5 w-5 text-red-600" />;
      case 'SKIPPED':
        return <ChevronRight className="h-5 w-5 text-slate-400" />;
      default:
        return <div className="h-5 w-5 rounded-full border-2 border-gray-300" />;
    }
  };

  const getMilestoneIcon = (type: PoMilestoneType) => {
    if (type.includes('SHIPPED') || type.includes('TRANSIT') || type.includes('DELIVERED')) {
      return <Truck className="h-4 w-4" />;
    }
    if (type.includes('PRODUCTION')) {
      return <Factory className="h-4 w-4" />;
    }
    if (type.includes('QUALITY') || type.includes('RECEIVED')) {
      return <FileCheck className="h-4 w-4" />;
    }
    return <Package className="h-4 w-4" />;
  };

  return (
    <div className="space-y-4">
      {milestones.map((milestone, index) => (
        <div key={milestone.id} className="flex gap-4">
          <div className="flex flex-col items-center">
            {getStatusIcon(milestone.status)}
            {index < milestones.length - 1 && (
              <div
                className={`w-0.5 h-full mt-2 ${
                  milestone.status === 'COMPLETED' ? 'bg-emerald-300' : 'bg-gray-200'
                }`}
              />
            )}
          </div>
          <div className="flex-1 pb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {getMilestoneIcon(milestone.type)}
                <span className="font-medium">{milestoneTypeLabels[milestone.type]}</span>
                <Badge className={milestoneStatusColors[milestone.status]}>
                  {milestone.status}
                </Badge>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreHorizontal className="h-4 w-4" />
                    <span className="sr-only">Open menu</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => onUpdateMilestone(milestone.id, { status: 'IN_PROGRESS' })}
                  >
                    Mark In Progress
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      onUpdateMilestone(milestone.id, {
                        status: 'COMPLETED',
                        actualDate: new Date().toISOString(),
                      })
                    }
                  >
                    Mark Completed
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onUpdateMilestone(milestone.id, { status: 'DELAYED' })}
                  >
                    Mark Delayed
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onUpdateMilestone(milestone.id, { status: 'SKIPPED' })}
                  >
                    Skip Milestone
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              {milestone.actualDate ? (
                <span>
                  Completed: {format(new Date(milestone.actualDate), 'MMM d, yyyy h:mm a')}
                </span>
              ) : milestone.plannedDate ? (
                <span>Planned: {format(new Date(milestone.plannedDate), 'MMM d, yyyy')}</span>
              ) : (
                <span>No date set</span>
              )}
            </div>
            {milestone.location && (
              <div className="text-sm text-muted-foreground">Location: {milestone.location}</div>
            )}
            {milestone.notes && (
              <div className="text-sm text-muted-foreground mt-1">{milestone.notes}</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
